import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Button from '../components/UI/Button';
import { Moon, Sun, Bot, ArrowRight, Check, FileText, Users, Download, UserPlus, FileEdit, Send, Lock } from 'lucide-react';
import { STRIPE_PRODUCTS } from '../stripe-config';
import { createCheckoutSession } from '../lib/stripe';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isAnnual, setIsAnnual] = useState(true);

  const features = [
    {
      icon: <Bot className="h-6 w-6 text-primary-500" />,
      title: "Génération IA Avancée",
      description: "Créez des factures et devis professionnels en quelques secondes grâce à notre IA."
    },
    {
      icon: <FileText className="h-6 w-6 text-primary-500" />,
      title: "Documents Personnalisables",
      description: "Personnalisez vos documents selon vos besoins avec notre éditeur intuitif."
    },
    {
      icon: <Users className="h-6 w-6 text-primary-500" />,
      title: "Gestion des Clients",
      description: "Gérez facilement votre base clients et accédez à leur historique."
    },
    {
      icon: <Download className="h-6 w-6 text-primary-500" />,
      title: "Export PDF",
      description: "Exportez vos documents en PDF de qualité professionnelle."
    }
  ];

  const steps = [
    {
      title: "Ajoutez vos clients",
      description: "Créez votre base de données clients avec leurs informations de facturation",
      icon: <UserPlus className="h-6 w-6 text-white" />,
    },
    {
      title: "Décrivez votre prestation",
      description: "Expliquez simplement votre travail, l'IA s'occupe du reste",
      icon: <Bot className="h-6 w-6 text-white" />,
    },
    {
      title: "Générez le document",
      description: "L'IA crée un document professionnel avec une mise en page soignée",
      icon: <FileText className="h-6 w-6 text-white" />,
    },
    {
      title: "Personnalisez",
      description: "Ajustez le contenu et les montants selon vos besoins",
      icon: <FileEdit className="h-6 w-6 text-white" />,
    },
    {
      title: "Envoyez",
      description: "Partagez le document par email ou téléchargez-le en PDF",
      icon: <Send className="h-6 w-6 text-white" />,
    }
  ];

  const sharedFeatures = [
    "Factures et devis",
    "Génération IA avancée",
    "Export PDF et ZIP",
    "Personnalisation avancée",
    "Tableau de bord avancé"
  ];

  const plans = [
    {
      name: "Accès Beta",
      features: [
        ...sharedFeatures,
        "Documents illimités",
        "Accès à toutes les fonctionnalités",
        "Aide au développement"
      ],
      cta: "S'inscrire gratuitement",
      popular: true,
      beta: true
    },
    {
      name: "Essentiel",
      monthlyPrice: 37,
      annualPrice: 31,
      features: [
        ...sharedFeatures,
        "Limité à 40 documents par mois"
      ],
      cta: "Bientôt disponible",
      popular: false,
      disabled: true,
      product: isAnnual ? STRIPE_PRODUCTS.ESSENTIAL_ANNUAL : STRIPE_PRODUCTS.ESSENTIAL
    },
    {
      name: "Premium",
      monthlyPrice: 47,
      annualPrice: 39,
      features: [
        ...sharedFeatures,
        "Documents illimités"
      ],
      cta: "Bientôt disponible",
      popular: false,
      disabled: true,
      product: isAnnual ? STRIPE_PRODUCTS.PREMIUM_ANNUAL : STRIPE_PRODUCTS.PREMIUM
    }
  ];

  const handleSubscribe = async (plan: typeof plans[0]) => {
    if (plan.beta) {
      navigate('/login', { 
        state: { 
          returnTo: '/',
          beta: true
        } 
      });
      return;
    }

    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          returnTo: '/',
          plan: plan.product.name 
        } 
      });
      return;
    }

    try {
      const url = await createCheckoutSession(
        plan.product.name as keyof typeof STRIPE_PRODUCTS,
        plan.product.priceId,
        `${window.location.origin}/app`,
        `${window.location.origin}/`
      );
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      if (error instanceof Error && error.message.includes('authenticate')) {
        navigate('/login', { 
          state: { 
            returnTo: '/',
            plan: plan.product.name 
          } 
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex-shrink-0">
                <img 
                  src={theme === 'dark' ? '/logoblanc.png' : '/logo.png'} 
                  alt="Invoice AI Logo" 
                  className="h-8"
                />
              </Link>

              <nav className="hidden md:flex items-center space-x-6">
                <a 
                  href="#features" 
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors font-medium"
                >
                  Fonctionnalités
                </a>
                <a 
                  href="#pricing" 
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors font-medium"
                >
                  Tarifs
                </a>
                <Link 
                  to="/contact" 
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors font-medium"
                >
                  Contact
                </Link>
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              <button
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              {isAuthenticated ? (
                <Button
                  variant="primary"
                  onClick={() => navigate('/app')}
                >
                  Dashboard
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => navigate('/login')}
                >
                  Connexion
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="pt-16">
        <div className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                  Simplifiez votre <span className="text-primary-500">facturation</span> avec l'IA
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                  Créez des factures et devis professionnels en quelques secondes. Gagnez du temps et concentrez-vous sur votre business.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    onClick={() => navigate('/login')}
                    leftIcon={<ArrowRight className="h-5 w-5" />}
                  >
                    Démarrer maintenant
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-lg overflow-hidden shadow-2xl">
                  <img 
                    src="/invoiceai.webp" 
                    alt="Invoice AI Dashboard" 
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-24 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Comment ça fonctionne ?
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                Créez des documents professionnels en quelques étapes simples
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg transform hover:scale-105 transition-transform duration-300"
                >
                  <div className="h-12 w-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="py-24 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Comment ça fonctionne ?
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                Créez des documents professionnels en quelques étapes simples
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              <div className="absolute left-[2.875rem] top-0 bottom-0 w-px bg-primary-200 dark:bg-primary-800" />

              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={index} className="relative flex items-start group">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-white text-xl font-bold shrink-0 transition-transform duration-300 group-hover:scale-110 z-10">
                      {index + 1}
                    </div>
                    
                    <div className="ml-6 flex-1">
                      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg transform transition-transform duration-300 group-hover:translate-x-1">
                        <div className="flex items-center mb-4">
                          <div className="bg-primary-500 text-white h-12 w-12 rounded-full flex items-center justify-center">
                            {step.icon}
                          </div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-12">
                <Button
                  size="lg"
                  onClick={() => navigate('/login')}
                  leftIcon={<ArrowRight className="h-5 w-5" />}
                >
                  Démarrer maintenant
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div id="pricing" className="py-24 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Des Prix Adaptés à Vos Besoins
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                Choisissez le plan qui vous convient le mieux
              </p>

              <div className="flex items-center justify-center gap-4 mb-12">
                <span className={`text-sm ${!isAnnual ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                  Mensuel
                </span>
                <button
                  onClick={() => setIsAnnual(!isAnnual)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    isAnnual ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  disabled
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isAnnual ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-sm ${isAnnual ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                  Annuel <span className="text-primary-500">(2 mois offerts)</span>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {plans.map((plan, index) => (
                  <div
                    key={index}
                    className={`
                      relative bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg
                      ${plan.popular ? 'border-2 border-primary-500' : 'border border-gray-200 dark:border-gray-700'}
                      ${plan.disabled ? 'opacity-50' : ''}
                    `}
                  >
                    {plan.popular && (
                      <div className="absolute top-0 right-0 bg-primary-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-sm font-medium">
                        Recommandé
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {plan.name}
                    </h3>
                    {!plan.beta && (
                      <div className="flex items-baseline mb-8">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                          {isAnnual ? plan.annualPrice : plan.monthlyPrice}€
                        </span>
                        <span className="text-gray-600 dark:text-gray-400 ml-2">
                          /mois
                        </span>
                      </div>
                    )}
                    {isAnnual && !plan.beta && (
                      <div className="mb-8 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-md">
                        <p className="text-sm text-primary-700 dark:text-primary-300">
                          Économisez {((plan.monthlyPrice || 0) - (plan.annualPrice || 0)) * 12}€ par an
                        </p>
                      </div>
                    )}
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <Check className="h-5 w-5 text-primary-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={plan.popular ? 'primary' : 'outline'}
                      fullWidth
                      size="lg"
                      onClick={() => handleSubscribe(plan)}
                      disabled={plan.disabled}
                      leftIcon={plan.disabled ? <Lock className="h-4 w-4" /> : undefined}
                    >
                      {plan.cta}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="bg-white dark:bg-gray-800 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="md:col-span-1">
                <img 
                  src={theme === 'dark' ? '/logoblanc.png' : '/logo.png'} 
                  alt="Invoice AI" 
                  className="h-8 mb-4"
                />
                <p className="text-gray-600 dark:text-gray-400">
                  Solution de facturation intelligente pour les professionnels.
                </p>
              </div>

              <div className="md:col-span-1">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Produit
                </h4>
                <ul className="space-y-2">
                  <li>
                    <a href="#features" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Fonctionnalités
                    </a>
                  </li>
                  <li>
                    <a href="#pricing" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Tarifs
                    </a>
                  </li>
                </ul>
              </div>

              <div className="md:col-span-1">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Support
                </h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/help" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Centre d'aide
                    </a>
                  </li>
                  <li>
                    <a href="/contact" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              <div className="md:col-span-1">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Légal
                </h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/privacy" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Confidentialité
                    </a>
                  </li>
                  <li>
                    <a href="/legal" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Mentions légales
                    </a>
                  </li>
                  <li>
                    <a href="/terms" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      CGV
                    </a>
                  </li>
                  <li>
                    <a href="/terms-of-use" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      CGU
                    </a>
                  </li>
                  <li>
                    <a href="/refund" className="text-gray-600 dark:text-gray-400 hover:text-primary-500">
                      Remboursements
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">
                © {new Date().getFullYear()} Invoice AI. Tous droits réservés.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Landing;